import { useState, useEffect, useCallback } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getGuestSessionId } from "@/lib/guestTracking";

// Safe, appropriate mood emojis
export const MOOD_EMOJIS = [
  { emoji: "😊", label: "Happy" },
  { emoji: "🤔", label: "Thinking" },
  { emoji: "😴", label: "Sleepy" },
  { emoji: "🔥", label: "On Fire" },
  { emoji: "😎", label: "Cool" },
  { emoji: "🤓", label: "Nerdy" },
  { emoji: "☕", label: "Caffeinated" },
  { emoji: "🚀", label: "Productive" },
  { emoji: "😅", label: "Stressed" },
  { emoji: "🎯", label: "Focused" },
  { emoji: "😢", label: "Sad" },
  { emoji: "😕", label: "Confused" },
  { emoji: "😤", label: "Frustrated" },
  { emoji: "🥳", label: "Celebrating" },
  { emoji: "🤯", label: "Mind Blown" },
] as const;

interface Mood {
  id: string;
  session_id: string;
  emoji: string;
  created_at: string;
}

const COOLDOWN_MS = 60_000; // 1 minute

export function useMoods() {
  const queryClient = useQueryClient();
  const sessionId = getGuestSessionId();
  const [lastSetTime, setLastSetTime] = useState<number>(() => {
    const stored = sessionStorage.getItem("mood_last_set");
    return stored ? parseInt(stored, 10) : 0;
  });

  // Fetch recent moods (last 5, excluding own)
  const { data: recentMoods = [] } = useQuery({
    queryKey: ["moods", "recent"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_moods")
        .select("id, session_id, emoji, created_at")
        .order("created_at", { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return (data || []) as Mood[];
    },
    staleTime: 10_000,
  });

  // Fetch own mood
  const { data: myMood } = useQuery({
    queryKey: ["moods", "mine", sessionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_moods")
        .select("id, session_id, emoji, created_at")
        .eq("session_id", sessionId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) throw error;
      return data as Mood | null;
    },
    enabled: !!sessionId,
  });

  // Set mood mutation
  const setMoodMutation = useMutation({
    mutationFn: async (emoji: string) => {
      const { error } = await supabase
        .from("user_moods")
        .insert({ session_id: sessionId, emoji });
      
      if (error) throw error;
    },
    onSuccess: () => {
      const now = Date.now();
      setLastSetTime(now);
      sessionStorage.setItem("mood_last_set", now.toString());
      queryClient.invalidateQueries({ queryKey: ["moods"] });
    },
  });

  // Calculate cooldown
  const timeUntilNextSet = Math.max(0, COOLDOWN_MS - (Date.now() - lastSetTime));
  const canSetMood = timeUntilNextSet === 0;

  // Subscribe to realtime updates
  useEffect(() => {
    const channel = supabase
      .channel("moods-realtime")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "user_moods" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["moods", "recent"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  // Get others' moods (exclude own, limit to 5)
  const otherMoods = recentMoods
    .filter((m) => m.session_id !== sessionId)
    .slice(0, 5);

  // Get all recent moods for display (limit to 5)
  const displayMoods = recentMoods.slice(0, 5);

  const setMood = useCallback((emoji: string) => {
    if (canSetMood) {
      setMoodMutation.mutate(emoji);
    }
  }, [canSetMood, setMoodMutation]);

  return {
    myMood,
    otherMoods,
    recentMoods: displayMoods,
    setMood,
    canSetMood,
    timeUntilNextSet,
    isSettingMood: setMoodMutation.isPending,
  };
}
